'use client';

import type { ComponentType } from 'react';
import { useState, useTransition } from 'react';
import { Bookmark, BookmarkCheck } from 'lucide-react';

import { Button } from '@kit/ui/button';
import { toast } from '@kit/ui/sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kit/ui/tooltip';
import { cn } from '@kit/ui/utils';

import { toggleBookmarkAction } from '../actions';
import type { BookmarkableType } from '../types';

interface BookmarkButtonProps {
  bookmarkableType: BookmarkableType;
  bookmarkableId: string;
  initialBookmarked?: boolean;
  organizationId?: string;
  variant?: 'icon' | 'button';
  size?: 'sm' | 'default' | 'lg';
  className?: string;
  onToggle?: (bookmarked: boolean, bookmarkId: string | null) => void;
  /**
   * Presentation overrides. The button persists to the same `bookmarks` table
   * regardless; these only change the icon, copy, and toasts so a surface can
   * frame bookmarking as e.g. "favorites" (heart) without forking the logic or
   * touching every other caller. All default to the bookmark presentation.
   */
  icon?: ComponentType<{ className?: string }>;
  activeIcon?: ComponentType<{ className?: string }>;
  /** Tooltip + aria label for the "add" state. */
  addLabel?: string;
  /** Tooltip + aria label for the "remove" state. */
  removeLabel?: string;
  /** Visible text for the `button` variant (add state). Defaults to "Bookmark". */
  buttonLabel?: string;
  /** Visible text for the `button` variant (active state). Defaults to "Bookmarked". */
  buttonActiveLabel?: string;
  addedToast?: string;
  removedToast?: string;
}

/**
 * A button component for toggling bookmarks on various entity types
 *
 * @example
 * ```tsx
 * // Icon variant (default)
 * <BookmarkButton
 *   bookmarkableType="professional_profile"
 *   bookmarkableId={profileId}
 *   initialBookmarked={isBookmarked}
 * />
 *
 * // Button variant with text
 * <BookmarkButton
 *   bookmarkableType="audition"
 *   bookmarkableId={auditionId}
 *   variant="button"
 * />
 * ```
 */
export function BookmarkButton({
  bookmarkableType,
  bookmarkableId,
  initialBookmarked = false,
  organizationId,
  variant = 'icon',
  size = 'default',
  className,
  onToggle,
  icon,
  activeIcon,
  addLabel = 'Add to bookmarks',
  removeLabel = 'Remove from bookmarks',
  buttonLabel = 'Bookmark',
  buttonActiveLabel = 'Bookmarked',
  addedToast = 'Added to bookmarks',
  removedToast = 'Removed from bookmarks',
}: BookmarkButtonProps) {
  const [isBookmarked, setIsBookmarked] = useState(initialBookmarked);
  const [isPending, startTransition] = useTransition();

  const handleToggle = () => {
    startTransition(async () => {
      const result = await toggleBookmarkAction({
        bookmarkable_type: bookmarkableType,
        bookmarkable_id: bookmarkableId,
        organization_id: organizationId,
      });

      if (result.success) {
        setIsBookmarked(result.data.bookmarked);
        onToggle?.(result.data.bookmarked, result.data.bookmark_id);

        if (result.data.bookmarked) {
          toast.success(addedToast);
        } else {
          toast.success(removedToast);
        }
      } else {
        toast.error(result.error || 'Failed to update bookmark');
      }
    });
  };

  // Presentation icons: `icon`/`activeIcon` override the bookmark default. When
  // only `icon` is supplied (e.g. a single Heart), use it for both states and
  // rely on `fill-current` to distinguish active.
  const InactiveIcon = icon ?? Bookmark;
  const ActiveIcon = activeIcon ?? icon ?? BookmarkCheck;
  const Icon = isBookmarked ? ActiveIcon : InactiveIcon;

  const iconSizes = {
    sm: 'h-4 w-4',
    default: 'h-5 w-5',
    lg: 'h-6 w-6',
  };

  const buttonSizes = {
    sm: 'h-8',
    default: 'h-9',
    lg: 'h-10',
  };

  if (variant === 'icon') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                buttonSizes[size],
                'w-auto px-2',
                isBookmarked && 'text-primary',
                className,
              )}
              onClick={handleToggle}
              disabled={isPending}
              aria-label={isBookmarked ? removeLabel : addLabel}
            >
              <Icon
                className={cn(
                  iconSizes[size],
                  isBookmarked && 'fill-current',
                  isPending && 'animate-pulse',
                )}
              />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {isBookmarked ? removeLabel : addLabel}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Button
      variant={isBookmarked ? 'secondary' : 'outline'}
      size={size}
      className={cn(className)}
      onClick={handleToggle}
      disabled={isPending}
    >
      <Icon
        className={cn(
          'mr-2',
          iconSizes[size],
          isBookmarked && 'fill-current',
          isPending && 'animate-pulse',
        )}
      />
      {isBookmarked ? buttonActiveLabel : buttonLabel}
    </Button>
  );
}
